class CartRemoveButton extends HTMLElement {
	constructor() {
		super();

		this.addEventListener("click", (event) => {
			event.preventDefault();

			const targetAriaLabel =
				event.target.getAttribute("aria-label") ||
				event.currentTarget.getAttribute("aria-label");

			const removeItemTitle = event.target.dataset.productTitle || event.currentTarget.dataset.productTitle || targetAriaLabel.split("Remove ")[1];

			const removeItemConfirmContainer = document.getElementById(
				"remove-item-confirm",
			);
			const removeItemConfirmOverlay = document.getElementById(
				"confirm-delete-overlay",
			);
			const removeItemTitleContainer =
				document.getElementById("remove-item-title");
			removeItemTitleContainer.innerText = removeItemTitle;

			const confirmedRemove = document.getElementById("item-remove-yes");
			const confirmedKeep = document.getElementById("item-remove-no");

			removeItemConfirmContainer.classList.remove("hidden");
			removeItemConfirmOverlay.classList.remove("hidden");
			removeItemConfirmOverlay.classList.add("block");

			new Promise((resolve, reject) => {
				function keepListener() {
					reject();
					confirmedKeep.removeEventListener("click", keepListener);
				}
				confirmedKeep.addEventListener("click", keepListener);

				function removeListener() {
					resolve();
					confirmedRemove.removeEventListener("click", removeListener);
				}
				confirmedRemove.addEventListener("click", removeListener);
			})
				.then(() => {
					const cartItems =
						this.closest("cart-items") || this.closest("cart-drawer-items");
					cartItems.updateQuantity(this.dataset.index, 0);
					removeItemConfirmContainer.classList.add("hidden");
					removeItemConfirmOverlay.classList.add("hidden");
				})
				.catch(() => {
					removeItemConfirmContainer.classList.add("hidden");
					removeItemConfirmOverlay.classList.add("hidden");
				});
		});
	}
}

customElements.define("cart-remove-button", CartRemoveButton);

class DiscountApplyButton extends HTMLElement {
	constructor() {
		super();
		this.button = this.querySelector("button");
		this.discountCodeInput = this.querySelector("input[name='discount-code']");
		this.discountErrorContainer = this.querySelector(
			"#discount-application-error-msg",
		);
		this.cart = document.querySelector("cart-drawer");
		this.changeEvent = new Event("change", { bubbles: true });
		this.cartAPIHandler = new CartAPIDiscountHandler();

		if (this.button) {
			this.button.addEventListener("click", this.onButtonClick.bind(this));
		}

		if (this.discountCodeInput) {
			this.discountCodeInput.addEventListener(
				"keydown",
				this.onInputKeydown.bind(this),
			);
		}

		const removeButtons = this.querySelectorAll(".remove-discount");
		removeButtons.forEach((removeButton) => {
			removeButton.addEventListener(
				"click",
				this.onRemoveDiscountClick.bind(this),
			);
		});
	}

	onInputKeydown(event) {
		if (event.key === "Enter") {
			event.preventDefault();
			this.onButtonClick(event);
		}
	}

	async onButtonClick(event) {
		event.preventDefault();
		
		if (this.discountErrorContainer) {
			this.discountErrorContainer.classList.add("hidden");
		}

		const newDiscountCode = this.discountCodeInput
			? this.discountCodeInput.value.trim()
			: "";

		if (!newDiscountCode) {
			if (this.discountErrorContainer) {
				this.discountErrorContainer.classList.remove("hidden");
			}
			return;
		}

		this.toggleLoadingEffect(true);

		try {
			const result = await this.cartAPIHandler.applyDiscountCode(newDiscountCode);
			
			if (result.success) {
				if (this.discountCodeInput) {
					this.discountCodeInput.value = "";
				}
				
				if (this.discountErrorContainer) {
					this.discountErrorContainer.classList.add("hidden");
				}

				await this.cartAPIHandler.refreshCartInterface();

			} else {
				if (this.discountErrorContainer) {
					this.discountErrorContainer.classList.remove("hidden");
				} 
			}
		} catch (error) {
			if (this.discountErrorContainer) {
				this.discountErrorContainer.classList.remove("hidden");
			}
		} finally {
			this.toggleLoadingEffect(false);
		}
	}

	async onRemoveDiscountClick(event) {
		event.preventDefault();

		const discountCode = event.currentTarget.dataset.discountCode.toLowerCase();

		if (!discountCode) {
			return;
		}

		this.toggleLoadingEffect(true);

		try {
			const result = await this.cartAPIHandler.removeDiscountCode(discountCode);
			
			if (result.success) {
				await this.cartAPIHandler.refreshCartInterface();
			}
			} catch (error) {
				console.error('Error removing discount:', error);
		} finally {
			this.toggleLoadingEffect(false);
		}
	}

	async updateCartDiscountCodes(discountCodes) {
		if (!discountCodes || discountCodes.length === 0) {
			return await this.cartAPIHandler.applyDiscountCode("");
		}
		
		const result = await this.cartAPIHandler.applyDiscountCode(discountCodes[0]);
		return {
			discountCodeResponse: {
				shouldThrowError: !result.success
			}
		};
	}

	async refreshCart() {
		await this.cartAPIHandler.refreshCartPage();
	}

	async refreshMinicart() {
		await this.cartAPIHandler.refreshMinicart();
	}

	toggleLoadingEffect(isLoading) {
		if (this.button) {
			this.button.disabled = isLoading;
			if (isLoading === true) {
				this.button.children[0].innerHTML = "";
			} else {
				this.button.children[0].innerHTML = "Apply";
			}
			this.button.classList.toggle("loading", isLoading);
		}
	}

	async fetchCartContents() {
		return await this.cartAPIHandler.getCartToken();
	}

	async fetchCurrentDiscountCodes() {
		return await this.cartAPIHandler.getAppliedDiscountCodes();
	}
}

customElements.define("discount-apply-button", DiscountApplyButton);

class CartItems extends HTMLElement {
	constructor() {
		super();
		this.lineItemStatusElement =
			document.getElementById("shopping-cart-line-item-status") ||
			document.getElementById("CartDrawer-LineItemStatus");

		const debouncedOnChange = debounce((event) => {
			this.onChange(event);
		}, ON_CHANGE_DEBOUNCE_TIMER);

		this.addEventListener("change", debouncedOnChange.bind(this));
	}

	cartUpdateUnsubscriber = undefined;

	connectedCallback() {
		this.cartUpdateUnsubscriber = subscribe(
			PUB_SUB_EVENTS.cartUpdate,
			(event) => {
				if (event.source === "cart-items") {
					return;
				}
				this.onCartUpdate();
			},
		);
	}

	disconnectedCallback() {
		if (this.cartUpdateUnsubscriber) {
			this.cartUpdateUnsubscriber();
		}
	}

	onChange(event) {
		this.updateQuantity(
			event.target.dataset.index,
			event.target.value,
			document.activeElement.getAttribute("name"),
			event.target.parentNode.dataset.productTitle,
		);
	}

	onCartUpdate() {
		fetch(`${routes.cart_url}?section_id=main-cart-items`, {
			headers: {
				"x-hava-alpa":
					"BF26981CA6970831906363B2BEAF1AFB0E469D90454E180ADA12802A012A0E72",
			},
		})
			.then((response) => response.text())
			.then((responseText) => {
				const html = new DOMParser().parseFromString(responseText, "text/html");
				const sourceQty = html.querySelector("cart-items");
				this.innerHTML = sourceQty.innerHTML;
			})
			.catch((e) => {
				console.error(e);
			});
	}

	getSectionsToRender() {
		return [
			{
				id: "main-cart-items",
				section: document.getElementById("main-cart-items").dataset.id,
				selector: ".js-contents",
			},
			{
				id: "cart-icon-bubble",
				section: "cart-icon-bubble",
				selector: ".shopify-section",
			},
			{
				id: "cart-live-region-text",
				section: "cart-live-region-text",
				selector: ".shopify-section",
			},
			{
				id: "main-cart-footer",
				section: document.getElementById("main-cart-footer").dataset.id,
				selector: ".js-contents",
			},
		];
	}

	updateQuantity(line, quantity, name, productName) {
		this.enableLoading(line);

		const body = JSON.stringify({
			line,
			quantity,
			sections: this.getSectionsToRender().map((section) => section.section),
			sections_url: window.location.pathname,
		});

		fetch(`${routes.cart_change_url}`, {
			...fetchConfig(),
			...{
				body,
				headers: {
					...fetchConfig().headers,
					"x-hava-alpa":
						"BF26981CA6970831906363B2BEAF1AFB0E469D90454E180ADA12802A012A0E72",
				},
			},
		})
			.then((response) => {
				return response.text();
			})
			.then((state) => {
				const parsedState = JSON.parse(state);

				const cartHelper = customElements.get("cart-helper");
				const currentProgressBar = document.querySelector(
					".pdp-thermometer .progress-bar-content-status",
				);

				if (currentProgressBar) {
					const freeShippingThresholdType = document.querySelector(
						".free-shipping-threshold-type",
					);
					const freeShippingThresholdOrderValue = document.querySelector(
						".free-shipping-threshold-order-value",
					);

					cartHelper.prototype.updateThermometer(
						parsedState.total_price,
						parsedState.item_count,
						currentProgressBar,
						freeShippingThresholdType,
						freeShippingThresholdOrderValue,
					);
				}

				const quantityElement =
					document.getElementById(`Quantity-${line}`) ||
					document.getElementById(`Drawer-quantity-${line}`);
				const items = document.querySelectorAll(".cart-item");

				if (parsedState.errors) {
					quantityElement.value = quantityElement.getAttribute("value");
					let errorMessage = "";
					if (Shopify.locale == "en") {
						errorMessage = `You already have all available ${productName} in your cart`;
					}
					if (Shopify.locale == "es-MX" || Shopify.locale == "es") {
						errorMessage = `Ya tienes todos los ${productName} disponibles en tu carrito`;
					}

					this.updateLiveRegions(line, errorMessage);
					const lineItemError =
						document.getElementById(`Line-item-error-${line}`) ||
						document.getElementById(`CartDrawer-LineItemError-${line}`);
					lineItemError.style.padding = "10px";
					lineItemError.previousElementSibling.classList.remove("loading");
					return;
				}

				const lineItemError =
					document.getElementById(`Line-item-error-${line}`) ||
					document.getElementById(`CartDrawer-LineItemError-${line}`);
				lineItemError.style.padding = "0px";

				this.classList.toggle("is-empty", parsedState.item_count === 0);
				const cartDrawerWrapper = document.querySelector("cart-drawer");
				const cartFooter = document.getElementById("main-cart-footer");

				if (cartFooter)
					cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
				if (cartDrawerWrapper)
					cartDrawerWrapper.classList.toggle(
						"is-empty",
						parsedState.item_count === 0,
					);

				this.getSectionsToRender().forEach((section) => {
					const elementToReplace =
						document
							.getElementById(section.id)
							.querySelector(section.selector) ||
						document.getElementById(section.id);
					elementToReplace.innerHTML = this.getSectionInnerHTML(
						parsedState.sections[section.section],
						section.selector,
					);
				});
				const updatedValue = parsedState.items[line - 1]
					? parsedState.items[line - 1].quantity
					: undefined;
				let message = "";
				if (
					items.length === parsedState.items.length &&
					updatedValue !== parseInt(quantityElement.value)
				) {
					if (typeof updatedValue === "undefined") {
						message = window.cartStrings.error;
					} else {
						message = window.cartStrings.quantityError.replace(
							"[quantity]",
							updatedValue,
						);
					}
				}
				this.updateLiveRegions(line, message);

				const lineItem =
					document.getElementById(`CartItem-${line}`) ||
					document.getElementById(`CartDrawer-Item-${line}`);
				if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
					cartDrawerWrapper
						? trapFocus(
								cartDrawerWrapper,
								lineItem.querySelector(`[name="${name}"]`),
							)
						: lineItem.querySelector(`[name="${name}"]`).focus();
				} else if (parsedState.item_count === 0 && cartDrawerWrapper) {
					trapFocus(
						cartDrawerWrapper.querySelector(".drawer__inner-empty"),
						cartDrawerWrapper.querySelector("a"),
					);
				} else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
					trapFocus(
						cartDrawerWrapper,
						document.querySelector(".cart-item__name"),
					);
				}
				publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });
			})
			.catch(() => {
				this.querySelectorAll(".loading-overlay").forEach((overlay) =>
					overlay.classList.add("hidden"),
				);
				const errors =
					document.getElementById("cart-errors") ||
					document.getElementById("CartDrawer-CartErrors");
				errors.textContent = window.cartStrings.error;
			})
			.finally(() => {
				this.disableLoading(line);
			});
	}

	updateLiveRegions(line, message) {
		const lineItemError =
			document.getElementById(`Line-item-error-${line}`) ||
			document.getElementById(`CartDrawer-LineItemError-${line}`);
		if (lineItemError)
			lineItemError.querySelector(".cart-item__error-text").innerHTML = message;

		this.lineItemStatusElement.setAttribute("aria-hidden", true);

		const cartStatus =
			document.getElementById("cart-live-region-text") ||
			document.getElementById("CartDrawer-LiveRegionText");
		cartStatus.setAttribute("aria-hidden", false);

		setTimeout(() => {
			cartStatus.setAttribute("aria-hidden", true);
		}, 1000);
	}

	getSectionInnerHTML(html, selector) {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector).innerHTML;
	}

	enableLoading(line) {
		const mainCartItems =
			document.getElementById("main-cart-items") ||
			document.getElementById("CartDrawer-CartItems");
		mainCartItems.classList.add("cart__items--disabled");

		const cartItemElements = this.querySelectorAll(
			`#CartItem-${line} .loading-overlay`,
		);
		const cartDrawerItemElements = this.querySelectorAll(
			`#CartDrawer-Item-${line} .loading-overlay`,
		);

		[...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
			overlay.classList.remove("hidden"),
		);

		document.activeElement.blur();
		this.lineItemStatusElement.setAttribute("aria-hidden", false);
	}

	disableLoading(line) {
		const mainCartItems =
			document.getElementById("main-cart-items") ||
			document.getElementById("CartDrawer-CartItems");
        if (mainCartItems)	mainCartItems.classList.remove("cart__items--disabled");

		const cartItemElements = this.querySelectorAll(
			`#CartItem-${line} .loading-overlay`,
		);
		const cartDrawerItemElements = this.querySelectorAll(
			`#CartDrawer-Item-${line} .loading-overlay`,
		);

		cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
		cartDrawerItemElements.forEach((overlay) =>
			overlay.classList.add("hidden"),
		);
	}
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-note")) {
	customElements.define(
		"cart-note",
		class CartNote extends HTMLElement {
			constructor() {
				super();

				this.addEventListener(
					"change",
					debounce((event) => {
						const body = JSON.stringify({ note: event.target.value });
						fetch(
							`${routes.cart_update_url}`,
							...fetchConfig(),
							...{
								body,
								headers: {
									...fetchConfig().headers,
									"x-hava-alpa":
										"BF26981CA6970831906363B2BEAF1AFB0E469D90454E180ADA12802A012A0E72",
								},
							},
						);
					}, ON_CHANGE_DEBOUNCE_TIMER),
				);
			}
		},
	);
}

class CartHelper {
	constructor() {}
	updateThermometer(
		totalPrice,
		quantity,
		currentProgressBar,
		freeShippingThresholdType,
		freeShippingThresholdOrderValue,
	) {
		let percentage = 0;
		let valueToCalc = null;

		if (!freeShippingThresholdOrderValue) return;

		if (
			freeShippingThresholdType &&
			freeShippingThresholdType.value == "by_order_value"
		) {
			valueToCalc = totalPrice;
		}

		if (
			freeShippingThresholdType &&
			freeShippingThresholdType.value == "by_item_count"
		) {
			valueToCalc = quantity;
		}
		percentage = Math.round(
			(valueToCalc / parseInt(freeShippingThresholdOrderValue.value)) * 100,
		);
		currentProgressBar.style.width = `${percentage > 100 ? 100 : percentage}%`;
	}
}

customElements.define("cart-helper", CartHelper);

// turn 'main' element into a flexbox
if (location.pathname == "/cart") {
	let mainContainer = document.getElementById("MainContent");
	mainContainer.classList.add("cart-container");
}

// add event listener to 'show order details', and persist it
function delegatedShowOrdersEventListener(event) {
	const showOrders = document.body.querySelector(".show-order-summary-mobile");
	const orderSummary = document.body.querySelector(".order-summary");

	if (showOrders && orderSummary && event.target === showOrders) {
		orderSummary.classList.toggle("hidden");
	}
}

document.addEventListener("DOMContentLoaded", () => {
	document.body.addEventListener("click", delegatedShowOrdersEventListener);
});

// Change position of 'discount code' depending on screen width
function moveElementBasedOnScreenWidth() {
	const screenWidth = window.innerWidth;
	const breakpoint = 768;
	const promoCodeContainer = document.querySelector(".promo-code-container");
	if (promoCodeContainer) {
		const cartItems = document.querySelector("cart-items");
		const footerContainer = document.querySelector(".footer-container");

		if (screenWidth >= breakpoint) {
			footerContainer.insertBefore(
				promoCodeContainer,
				footerContainer.firstChild,
			);
		} else {
			cartItems.parentNode.insertBefore(
				promoCodeContainer,
				cartItems.nextSibling,
			);
		}
	}
}

moveElementBasedOnScreenWidth();

window.addEventListener("resize", moveElementBasedOnScreenWidth);

// Remove 'help text' from cart on mobile, when user makes cart empty without refresh
document.addEventListener("DOMContentLoaded", function () {
	function toggleDisplayBasedOnPrevSibling() {
		const elements = document.querySelectorAll(".help-text-cart");
		elements.forEach((element) => {
			const prevSibling = element.previousElementSibling;
			if (prevSibling && prevSibling.classList.contains("is-empty")) {
				element.style.display = "none";
			} else {
				element.style.display = "";
			}
		});
	}

	function removeCartItemsWhenEmpty() {
		const cartItemsElement = document.querySelector("cart-items.is-empty");
		const freeShippingWidget = document.querySelector(
			".free-shipping-widget-container",
		);
		const mainCartFooter = document.getElementById("main-cart-footer");
		if (cartItemsElement) {
			cartItemsElement.parentNode.style.display = "none";
			mainCartFooter.parentNode.style.margin = "auto";
			mainCartFooter.parentNode.style.width = "auto";
			if (freeShippingWidget) {
				freeShippingWidget.style.opacity = "0";
			}
		}
	}

	toggleDisplayBasedOnPrevSibling();
	removeCartItemsWhenEmpty();

	const observer = new MutationObserver((mutationsList, observer) => {
		for (const mutation of mutationsList) {
			if (mutation.type === "childList") {
				toggleDisplayBasedOnPrevSibling();
				removeCartItemsWhenEmpty();
			}
		}
	});

	const cartTargetNode = document.body;

	const cartConfig = { attributes: false, childList: true, subtree: true };

	observer.observe(cartTargetNode, cartConfig);
});
